import { Button } from "@/components/ui/button";
import { Globe2, MapPin, Sparkles, Bot, Cog, TrendingUp } from "lucide-react";
import expansionAsset from "@/assets/global-expansion/shaun-booysen-global-expansion.png.asset.json";
import { TEAM_BOOKING_URL } from "@/lib/teamData";

const pillars = [
  { icon: Sparkles, label: "Artificial Intelligence" },
  { icon: Bot, label: "Commercial Robotics" },
  { icon: Cog, label: "Business Automation" },
  { icon: Globe2, label: "Global Innovation" },
  { icon: TrendingUp, label: "International Expansion" },
];

const GlobalExpansionSection = () => {
  return (
    <section
      id="south-africa-expansion"
      aria-labelledby="global-expansion-heading"
      className="scroll-mt-32 py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl">
        <div
          className="overflow-hidden rounded-[28px] border border-[hsl(var(--line))]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 15% 0%, hsl(var(--primary) / 0.22), transparent 40%), radial-gradient(circle at 85% 100%, hsl(43 75% 55% / 0.14), transparent 45%), linear-gradient(180deg, #05111f 0%, #02060d 100%)",
            boxShadow:
              "0 0 0 1px hsl(var(--line)), 0 0 48px hsl(var(--primary) / 0.18), 0 24px 80px rgba(0,0,0,0.55)",
          }}
        >
          <div className="grid gap-10 p-6 md:p-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:items-center lg:gap-12">
            <div className="order-2 lg:order-1">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[11px] font-semibold tracking-[0.24em] text-primary">
                <Globe2 className="h-3.5 w-3.5" />
                NEIL AI GLOBAL EXPANSION
              </div>

              <h2
                id="global-expansion-heading"
                className="font-orbitron text-3xl font-bold leading-tight text-foreground md:text-5xl"
              >
                Neil AI Expands Into <span className="text-primary">Cape Town, South Africa</span>
              </h2>

              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
                Building the future of AI, robotics, and business automation across continents. Neil AI Global Solutions
                is extending its artificial intelligence, commercial robotics, consulting, implementation, and
                workforce-transformation presence into the African market.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-background/60 px-3 py-1 text-xs font-semibold text-primary">
                  <MapPin className="h-3.5 w-3.5" /> Cape Town, South Africa
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-background/60 px-3 py-1 text-xs font-semibold text-accent">
                  🇿🇦 Africa Expansion Leadership
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(43_75%_55%/0.5)] bg-[hsl(43_75%_55%/0.08)] px-3 py-1 text-xs font-semibold text-[hsl(43_85%_65%)]">
                  Global Expansion 2026
                </span>
              </div>

              <div className="mt-6 rounded-2xl border border-[hsl(var(--line))] bg-background/40 p-5 backdrop-blur-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">Regional Director</p>
                <p className="mt-1 font-orbitron text-2xl font-bold text-foreground">Shaun Booysen</p>
                <p className="text-sm text-muted-foreground">
                  Director of Operations · Senior Business Transformation Advisor
                </p>
                <blockquote className="mt-3 border-l-2 border-primary/60 pl-3 text-sm italic text-foreground/80">
                  “Innovation has no borders. Together we’re building the future of AI across continents.”
                </blockquote>
              </div>

              <ul className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {pillars.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="flex items-center gap-2 rounded-xl border border-[hsl(var(--line))] bg-background/50 px-3 py-2 text-xs font-medium text-foreground/90"
                  >
                    <Icon className="h-3.5 w-3.5 text-primary" />
                    {label}
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  size="lg"
                  className="min-h-12 rounded-full bg-primary px-7 text-primary-foreground shadow-[0_0_30px_hsl(var(--primary)/0.4)] hover:bg-primary/90"
                >
                  <a href={TEAM_BOOKING_URL} target="_blank" rel="noopener noreferrer">
                    Partner With Neil AI Africa
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="min-h-12 rounded-full border-accent/50 bg-background/50 px-7 text-accent hover:bg-accent/10 hover:text-accent"
                >
                  <a href="#team">Meet The Global Team</a>
                </Button>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="relative overflow-hidden rounded-[24px] border border-white/10 bg-black shadow-[0_20px_80px_-20px_rgba(0,163,255,0.45)]">
                <img
                  src={expansionAsset.url}
                  alt="Shaun Booysen — Director, Cape Town, South Africa. Neil AI Global Solutions expands into South Africa. Automate. Elevate. Dominate."
                  className="h-auto w-full object-contain"
                  loading="lazy"
                />
              </div>
              <p className="mt-3 text-center text-xs text-muted-foreground">
                Neil AI Global Solutions · Global Expansion 2026 — North Carolina, USA ↔ Cape Town, South Africa
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlobalExpansionSection;
